/**
 * Creazione rapida di un fornitore o cliente in un dialog, per non
 * interrompere il flusso di compilazione di un preventivo quando il
 * soggetto cercato in un picker (Combobox) non esiste ancora in anagrafica.
 *
 * - tipo='fornitore' -> POST /api/v1/fornitori (tipo forzato lato server,
 *   richiede solo autenticazione).
 * - tipo='cliente'    -> POST /api/v1/soggetti con {tipo:'cliente', ...}
 *   (richiede permesso fornitori.modifica lato server; il chiamante deve
 *   gia' gatare la visibilita' dell'affordance "Crea nuovo cliente" con
 *   hasPermesso('fornitori.modifica') prima di aprire questo dialog).
 *
 * Form compatto orientato al caso azienda (fornitore tipico): ragione
 * sociale, P.IVA, indirizzo completo, contatti. Il checkbox "E' una
 * persona fisica" (default OFF) commuta a nome/cognome + codice fiscale
 * per i casi meno frequenti. I dettagli restanti (PEC, SDI, ...) restano
 * nel form dedicato (FornitoreForm/ClienteForm), raggiungibile in seguito
 * dalla scheda.
 */

import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@floraltech/ui';

/** Soggetto appena creato, cosi' come restituito dal backend (201). */
export interface SoggettoCreato {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  partita_iva: string | null;
  email: string | null;
}

interface QuickCreateFormData {
  ragione_sociale: string;
  nome: string;
  cognome: string;
  partita_iva: string;
  codice_fiscale: string;
  indirizzo: string;
  civico: string;
  cap: string;
  comune: string;
  provincia: string;
  email: string;
  telefono: string;
}

const EMPTY_FORM: QuickCreateFormData = {
  ragione_sociale: '',
  nome: '',
  cognome: '',
  partita_iva: '',
  codice_fiscale: '',
  indirizzo: '',
  civico: '',
  cap: '',
  comune: '',
  provincia: '',
  email: '',
  telefono: '',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipo: 'fornitore' | 'cliente';
  onCreated: (soggetto: SoggettoCreato) => void;
  /** Testo digitato dall'utente nel picker: precompila la ragione sociale. */
  presetNome?: string;
}

export function SoggettoQuickCreateDialog({ open, onOpenChange, tipo, onCreated, presetNome }: Props) {
  const [form, setForm] = useState<QuickCreateFormData>(EMPTY_FORM);
  const [personaFisica, setPersonaFisica] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, ragione_sociale: (presetNome ?? '').trim() });
      setPersonaFisica(false);
      setErrore(null);
    }
    // Il form va reinizializzato solo all'apertura, non ad ogni battitura in presetNome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (field: keyof QuickCreateFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTogglePersonaFisica = (checked: boolean) => {
    setPersonaFisica(checked);
    // Svuota i campi del modo non piu' visibile, per non inviare residui nascosti.
    if (checked) {
      setForm((prev) => ({ ...prev, ragione_sociale: '' }));
    } else {
      setForm((prev) => ({ ...prev, nome: '', cognome: '', codice_fiscale: '' }));
    }
  };

  const puoSalvare = personaFisica
    ? Boolean(form.nome.trim()) || Boolean(form.cognome.trim())
    : Boolean(form.ragione_sociale.trim());

  const titolo = tipo === 'fornitore' ? 'Crea nuovo fornitore' : 'Crea nuovo cliente';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!puoSalvare || saving) return;
    setSaving(true);
    setErrore(null);

    const payload: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(form) as [string, string][]) {
      payload[key] = value.trim() || null;
    }
    if (tipo === 'cliente') payload.tipo = 'cliente';

    const url = tipo === 'fornitore' ? '/api/v1/fornitori' : '/api/v1/soggetti';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (response.status === 409) {
          setErrore(body?.detail || 'Esiste gia\' un soggetto con questi dati (P.IVA o codice fiscale duplicati).');
        } else if (response.status === 403) {
          setErrore(body?.detail || 'Permesso mancante per creare questo soggetto.');
        } else if (response.status === 422) {
          setErrore(body?.detail || 'Dati non validi: controlla i campi compilati.');
        } else {
          setErrore(body?.detail || `Errore ${response.status}`);
        }
        return;
      }
      const data: SoggettoCreato = await response.json();
      onCreated(data);
      onOpenChange(false);
      setForm(EMPTY_FORM);
    } catch {
      setErrore('Errore di rete: verifica la connessione e riprova.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{titolo}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              id="qc-persona-fisica"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
              checked={personaFisica}
              onChange={(e) => handleTogglePersonaFisica(e.target.checked)}
            />
            <label htmlFor="qc-persona-fisica" className="text-sm text-gray-700">
              E&apos; una persona fisica
            </label>
          </div>

          {personaFisica ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="qc-nome">Nome</Label>
                <Input
                  id="qc-nome"
                  value={form.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="qc-cognome">Cognome</Label>
                <Input
                  id="qc-cognome"
                  value={form.cognome}
                  onChange={(e) => handleChange('cognome', e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="qc-ragione-sociale">Ragione sociale</Label>
              <Input
                id="qc-ragione-sociale"
                value={form.ragione_sociale}
                onChange={(e) => handleChange('ragione_sociale', e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qc-piva">Partita IVA</Label>
              <Input
                id="qc-piva"
                value={form.partita_iva}
                onChange={(e) => handleChange('partita_iva', e.target.value)}
                maxLength={11}
                placeholder="11 cifre senza IT"
              />
            </div>
            {personaFisica && (
              <div>
                <Label htmlFor="qc-cf">Codice fiscale</Label>
                <Input
                  id="qc-cf"
                  value={form.codice_fiscale}
                  onChange={(e) => handleChange('codice_fiscale', e.target.value)}
                  maxLength={16}
                />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="qc-indirizzo">Indirizzo</Label>
            <div className="flex gap-2">
              <Input
                id="qc-indirizzo"
                className="flex-1"
                value={form.indirizzo}
                onChange={(e) => handleChange('indirizzo', e.target.value)}
                placeholder="Via / piazza"
              />
              <Input
                id="qc-civico"
                className="w-24"
                value={form.civico}
                onChange={(e) => handleChange('civico', e.target.value)}
                placeholder="Civico"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="qc-cap">CAP</Label>
              <Input
                id="qc-cap"
                value={form.cap}
                onChange={(e) => handleChange('cap', e.target.value)}
                maxLength={5}
              />
            </div>
            <div>
              <Label htmlFor="qc-comune">Citta&apos;</Label>
              <Input
                id="qc-comune"
                value={form.comune}
                onChange={(e) => handleChange('comune', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="qc-provincia">Provincia</Label>
              <Input
                id="qc-provincia"
                value={form.provincia}
                onChange={(e) => handleChange('provincia', e.target.value)}
                maxLength={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qc-email">Email</Label>
              <Input
                id="qc-email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="qc-telefono">Telefono</Label>
              <Input
                id="qc-telefono"
                value={form.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
              />
            </div>
          </div>

          {!puoSalvare && (
            <p className="text-xs text-muted-foreground">
              {personaFisica
                ? 'Compila almeno nome oppure cognome.'
                : 'Compila la ragione sociale.'}
            </p>
          )}
          {errore && <p className="text-sm text-destructive">{errore}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving || !puoSalvare}>
              {saving ? 'Creazione...' : 'Crea'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
